import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Page, PageHeader, Card, StatusBadge, Loading, shortDate } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Github, ExternalLink, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { GraphiteScope } from "@/components/Graphite";

// GitHub creates the App from a manifest the browser posts to it; the control plane then trades the
// code GitHub sends back for the App's keys, so nobody copies them.
function postManifest({ post_url, manifest }) {
  const form = document.createElement("form");
  form.method = "post";
  form.action = post_url;
  const input = document.createElement("input");
  input.type = "hidden";
  input.name = "manifest";
  input.value = JSON.stringify(manifest);
  form.appendChild(input);
  document.body.appendChild(form);
  form.submit();
}

const PERMISSIONS = [
  "Contents: read and write, to push session branches.",
  "Pull requests: read and write, to open one when a session is submitted.",
  "Metadata: read.",
  "Public, so any company can install it on the repositories it chooses. No webhooks.",
];

// design_handoff_crewbench/screens/GitHub App.dc.html: the platform's GitHub App, created once from a manifest.
export default function GitHubApp() {
  const [org, setOrg] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["admin-github-app"], queryFn: async () => (await api.get("/admin/github-app")).data });
  const create = useMutation({
    mutationFn: async () => (await api.post("/admin/github-app/manifest", { origin: window.location.origin, organization: org.trim() || null })).data,
    onSuccess: postManifest,
    onError: (e) => toast.error(apiErr(e)),
  });
  const app = data?.app;
  const title = "How companies connect GitHub: one popup, no tokens or deploy keys.";
  if (isLoading) return <Page className="gap-5"><PageHeader eyebrow="Platform · GitHub App" title={title} /><Loading /></Page>;
  const row = (label, value, mono, last) => (
    <div className={cn("grid grid-cols-[120px_minmax(0,1fr)] gap-3 py-2 border-t", last && "border-b")}>
      <span className="text-sm text-muted-foreground">{label}</span><span className={mono ? "font-mono text-sm" : ""}>{value}</span>
    </div>
  );
  return (
    <Page className="gap-5" data-testid="admin-github-app">
      <PageHeader eyebrow="Platform · GitHub App" title={title} status={app ? `The ${app.name} app is set up.` : "It hasn't been created yet."} />
      {app ? (
        <Card className="max-w-[640px] p-5 flex flex-col gap-4" data-testid="github-app-ready">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="flex items-center gap-2.5 font-display text-[22px] leading-[1.25] tracking-[0.02em] font-semibold"><Github className="w-5 h-5" />{app.name}</span>
            <StatusBadge label="Ready" tone="info" />
          </div>
          <div className="flex flex-col tabular-nums">
            {row("Slug", app.slug, true)}{row("App ID", app.app_id, true)}{row("Owned by", app.owner)}{row("Created", shortDate(app.created_at, true), false, true)}
          </div>
          <span className="text-sm text-muted-foreground [text-wrap:pretty]">Founders connect it from Settings → GitHub. Its private key and client secret are stored encrypted and never shown.</span>
          <Button asChild variant="outline" className="self-start"><a href={app.html_url} target="_blank" rel="noreferrer"><ExternalLink />Open on GitHub</a></Button>
        </Card>
      ) : (
        <Card className="max-w-[640px] p-5 flex flex-col gap-4">
          <span className="text-gr-ink-secondary [text-wrap:pretty]">Create the platform's GitHub App once. GitHub shows you the app it will create: its name, permissions and redirect addresses. You confirm, and the keys come straight back here, encrypted.</span>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="gh-org">GitHub organization to own the app (optional)</Label>
            <Input id="gh-org" placeholder="Leave empty for your personal account" value={org} onChange={(e) => setOrg(e.target.value)} data-testid="github-app-org" />
          </div>
          <span className="text-sm text-muted-foreground [text-wrap:pretty]">GitHub sends you back to {window.location.origin}, so create it from the address founders use.</span>
          <Button className="self-start" onClick={() => create.mutate()} disabled={create.isPending} data-testid="github-app-create"><Github />Create GitHub App</Button>
        </Card>
      )}
      <section className="max-w-[640px] flex flex-col gap-2">
        <span className="font-display text-[15px] tracking-[0.02em] font-semibold">What the app can do</span>
        <div className="flex flex-col gap-1.5">
          {PERMISSIONS.map((t) => <span key={t} className="flex items-start gap-2"><Check className="w-3.5 h-3.5 mt-[3px] shrink-0 text-muted-foreground" /><span className="[text-wrap:pretty]">{t}</span></span>)}
        </div>
      </section>
    </Page>
  );
}

// GitHub's redirect after it created the App (/admin/github-app/created?code=…&state=…).
export function GitHubAppCreated() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return; // the code works once
    ran.current = true;
    api.post("/admin/github-app/created", { code: params.get("code") || "", state: params.get("state") || "" })
      .then(() => { qc.invalidateQueries({ queryKey: ["admin-github-app"] }); toast.success("GitHub App created."); })
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => navigate("/admin/github-app", { replace: true }));
  }, [params, navigate, qc]);
  return <GraphiteScope><Loading /></GraphiteScope>;
}
